import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video,
  VideoOff,
  Users,
  Clock,
  CheckCircle,
  RefreshCw,
  User,
  Lock,
  LogOut,
  Play,
  ShoppingCart,
  XCircle,
  ArrowLeft,
} from 'lucide-react';
import { useLiveSession, type QueueEntry, type ActiveSession } from '../../hooks/live-shopping';
import { useTenant } from '../../contexts/TenantContext';
import { api } from '../../services/api';

interface Vendor {
  id: string;
  name: string;
  role: string;
  active?: boolean;
}

export default function LiveShoppingDashboard() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const hasConnectedRef = useRef(false);

  // Vendor selection state
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoadingVendors, setIsLoadingVendors] = useState(true);

  // Live session state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vendorId = selectedVendor?.id || '';

  // Sauvegarder le vendorId en sessionStorage pour la session Live Shopping
  useEffect(() => {
    if (vendorId) {
      sessionStorage.setItem('live_shopping_vendor_id', vendorId);
    }
  }, [vendorId]);

  const {
    isConnected,
    connect,
    disconnect,
    isAvailable,
    setAvailable,
    queue,
    acceptQueueEntry,
    activeSessions,
    refreshActiveSessions,
  } = useLiveSession({
    role: 'vendor',
    vendorId,
    onSessionAssigned: (sessionId) => {
      navigate(`/live-shopping/session/${sessionId}/${vendorId}`);
    },
  });

  // Load vendors on mount
  useEffect(() => {
    const loadVendors = async () => {
      if (!tenant?.id) return;

      setIsLoadingVendors(true);
      try {
        const data = await api.listVendors(tenant.id);
        // Tous les vendeurs actifs peuvent faire du Live Shopping
        setVendors(data.filter((v: Vendor) => v.active !== false));
      } catch (err) {
        console.error('Failed to load vendors:', err);
      } finally {
        setIsLoadingVendors(false);
      }
    };
    loadVendors();
  }, [tenant?.id]);

  // Connect when vendor is selected and verified
  useEffect(() => {
    if (!vendorId) return;
    if (hasConnectedRef.current) return;
    hasConnectedRef.current = true;

    connect().catch((err) => {
      setError(err.message);
      hasConnectedRef.current = false;
    });
  }, [connect, vendorId]);

  // Verify PIN
  const handleVerifyPin = async () => {
    if (!selectedVendor || pin.length < 4) return;

    setIsVerifying(true);
    setPinError(null);

    try {
      const result = await api.verifyVendorPin(selectedVendor.id, pin);
      if (result.valid) {
        // PIN valid - vendor is now selected
        setPinError(null);
      } else {
        setPinError('PIN incorrect');
        setSelectedVendor(null);
      }
    } catch (err) {
      setPinError('Erreur de vérification');
      setSelectedVendor(null);
    } finally {
      setIsVerifying(false);
      setPin('');
    }
  };

  // Logout vendor
  const handleLogout = () => {
    disconnect();
    setSelectedVendor(null);
    hasConnectedRef.current = false;
  };

  const handleToggleAvailability = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await setAvailable(!isAvailable);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptEntry = async (entry: QueueEntry) => {
    setIsLoading(true);
    setError(null);
    try {
      const { sessionId } = await acceptQueueEntry(entry.id);
      navigate(`/live-shopping/session/${sessionId}/${vendorId}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Rejoindre une session active (reprise après déconnexion)
  const handleRejoinSession = (session: ActiveSession) => {
    navigate(`/live-shopping/session/${session.id}/${vendorId}`);
  };

  // Clôturer une session abandonnée
  const handleCloseSession = async (session: ActiveSession) => {
    if (!confirm(`Voulez-vous vraiment clôturer la session de ${session.customer?.name || 'ce client'} ?\n\nCette action est irréversible.`)) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await api.closeAbandonedSession(session.id);
      // Rafraîchir la liste
      await refreshActiveSessions();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculer la durée depuis le début de la session
  const getSessionDuration = (session: ActiveSession): string => {
    const startedAt = session.started_at ? new Date(session.started_at) : new Date(session.created_at);
    const now = new Date();
    const diffMs = now.getTime() - startedAt.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin} min`;
    const hours = Math.floor(diffMin / 60);
    const mins = diffMin % 60;
    return `${hours}h${mins.toString().padStart(2, '0')}`;
  };

  // Vérifier si une session est "zombie" (plus de 2h)
  const isZombieSession = (session: ActiveSession): boolean => {
    const startedAt = session.started_at ? new Date(session.started_at) : new Date(session.created_at);
    const now = new Date();
    const diffMs = now.getTime() - startedAt.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours > 2;
  };

  // If no vendor selected, show vendor selection
  if (!selectedVendor) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Video className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Live Shopping</h1>
            <p className="text-gray-600 mt-1">
              Sélectionnez votre profil vendeur
            </p>
          </div>

          {/* Vendor Selection */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Qui êtes-vous ?
            </h2>

            <div className="space-y-2">
              {isLoadingVendors ? (
                <p className="text-gray-500 text-center py-4">
                  Chargement des vendeurs...
                </p>
              ) : vendors.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  Aucun vendeur disponible
                </p>
              ) : (
                vendors.map((vendor) => (
                  <button
                    key={vendor.id}
                    onClick={() => setSelectedVendor(vendor)}
                    className="w-full flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-green-500 hover:bg-green-50 transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold">
                      {vendor.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{vendor.name}</p>
                      <p className="text-sm text-gray-500">{vendor.role}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If vendor selected but not yet verified, show PIN input
  if (selectedVendor && !isConnected && !hasConnectedRef.current) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Bonjour {selectedVendor.name}
            </h1>
            <p className="text-gray-600 mt-1">Entrez votre PIN pour continuer</p>
          </div>

          {/* PIN Input */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            {pinError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {pinError}
              </div>
            )}

            <div className="mb-4">
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="••••"
                className="w-full text-center text-3xl tracking-[0.5em] py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && pin.length >= 4) {
                    handleVerifyPin();
                  }
                }}
              />
            </div>

            <button
              onClick={handleVerifyPin}
              disabled={pin.length < 4 || isVerifying}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isVerifying ? 'Vérification...' : 'Valider'}
            </button>

            <button
              onClick={() => setSelectedVendor(null)}
              className="w-full mt-3 py-2 text-gray-600 hover:text-gray-900 text-sm"
            >
              ← Changer de vendeur
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main dashboard (vendor connected)
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/pos')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Retour au workspace"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Video className="w-7 h-7 text-green-600" />
                Live Shopping
              </h1>
              <p className="text-gray-600 mt-1">
                Accompagnez vos clients en direct avec la vidéo
              </p>
            </div>
          </div>

          {/* Vendor info + logout */}
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="font-medium text-gray-900">{selectedVendor.name}</p>
              <p className="text-sm text-gray-500">
                {isConnected ? (
                  <span className="text-green-600">● Connecté</span>
                ) : (
                  <span className="text-yellow-600">● Connexion...</span>
                )}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Déconnexion"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Status Toggle */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Mon statut</h2>
              <p className="text-sm text-gray-600">
                {isAvailable
                  ? 'Vous recevez les demandes de session live'
                  : 'Activez pour recevoir des demandes'}
              </p>
            </div>

            <button
              onClick={handleToggleAvailability}
              disabled={!isConnected || isLoading}
              className={`
                relative inline-flex h-12 w-24 items-center rounded-full transition-colors
                ${isAvailable
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-gray-300 hover:bg-gray-400'}
                ${(!isConnected || isLoading) && 'opacity-50 cursor-not-allowed'}
              `}
            >
              <span
                className={`
                  inline-flex items-center justify-center h-10 w-10 rounded-full bg-white shadow-sm transition-transform
                  ${isAvailable ? 'translate-x-12' : 'translate-x-1'}
                `}
              >
                {isAvailable ? (
                  <Video className="w-5 h-5 text-green-600" />
                ) : (
                  <VideoOff className="w-5 h-5 text-gray-500" />
                )}
              </span>
            </button>
          </div>

          {isAvailable && (
            <div className="mt-4 flex items-center gap-2 text-green-700 bg-green-50 rounded-lg px-4 py-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              <span className="font-medium">En ligne</span>
              <span className="text-green-600">- En attente de clients</span>
            </div>
          )}
        </div>

        {/* Sessions actives (à rejoindre) */}
        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Play className="w-5 h-5 text-orange-500" />
              Sessions en cours
              {activeSessions.length > 0 && (
                <span className="bg-orange-100 text-orange-800 text-sm font-medium px-2 py-0.5 rounded-full">
                  {activeSessions.length}
                </span>
              )}
            </h2>
            <button
              onClick={() => refreshActiveSessions()}
              className="text-gray-500 hover:text-gray-700 p-2"
              title="Rafraîchir"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {activeSessions.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Play className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Aucune session en cours à reprendre</p>
            </div>
          ) : (
            <ul className="divide-y">
              {activeSessions.map((session) => (
                <li
                  key={session.id}
                  className="p-4 hover:bg-orange-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-700 font-bold">
                        {session.customer?.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {session.customer?.name || 'Client inconnu'}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {getSessionDuration(session)}
                          </span>
                          <span className="flex items-center gap-1">
                            <ShoppingCart className="w-4 h-4" />
                            {session.item_count} article{session.item_count > 1 ? 's' : ''}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            session.status === 'active' ? 'bg-green-100 text-green-700' :
                            session.status === 'checkout' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {session.status === 'active' ? 'En cours' :
                             session.status === 'checkout' ? 'Checkout' : 'En attente'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRejoinSession(session)}
                        disabled={isLoading}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-orange-600 text-white hover:bg-orange-700"
                      >
                        <Play className="w-5 h-5" />
                        Rejoindre
                      </button>
                      <button
                        onClick={() => handleCloseSession(session)}
                        disabled={isLoading}
                        className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-colors ${
                          isZombieSession(session)
                            ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={isZombieSession(session) ? 'Session abandonnée (> 2h) - Clôturer' : 'Clôturer la session'}
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Queue */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5" />
              File d'attente
              {queue.length > 0 && (
                <span className="bg-green-100 text-green-800 text-sm font-medium px-2 py-0.5 rounded-full">
                  {queue.length}
                </span>
              )}
            </h2>
            <button
              onClick={() => connect()}
              className="text-gray-500 hover:text-gray-700 p-2"
              title="Rafraîchir"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {queue.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Aucune demande en attente</p>
              <p className="text-sm mt-1">
                Les demandes de vos clients apparaîtront ici
              </p>
            </div>
          ) : (
            <ul className="divide-y">
              {queue.map((entry) => (
                <li
                  key={entry.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold">
                        {entry.position}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {entry.customer_name}
                        </p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          En attente depuis {entry.wait_time_minutes} min
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleAcceptEntry(entry)}
                        disabled={isLoading || !isAvailable}
                        className={`
                          inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
                          ${isAvailable
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'}
                        `}
                      >
                        <CheckCircle className="w-5 h-5" />
                        Accepter
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-900 mb-3">
            Comment ça marche ?
          </h3>
          <ol className="space-y-2 text-blue-800">
            <li className="flex items-start gap-2">
              <span className="font-bold">1.</span>
              <span>Activez votre statut "Disponible"</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">2.</span>
              <span>Les clients peuvent demander une session live</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">3.</span>
              <span>Acceptez une demande pour démarrer la vidéo</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">4.</span>
              <span>Filmez les produits et constituez le panier ensemble</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">5.</span>
              <span>Finalisez la commande avec paiement wallet</span>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
